import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { formatPrice as formatAmount } from '@/lib/format'

import { useTheme } from '@/lib/hooks'
import { sendOrder } from '@/services/ordersApi'

import { setCurrentModal, setPaymentMethod, setPaymentStatus, finalTotalSelector } from '../../store/checkoutSlice'
import {
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalTotal,
  ModalWrapper,
  PaymentMethodButton,
  PaymentMethods,
  PaymentProcessingText,
  ProcessingRing,
  ProcessingSpinner,
  CloseButton
} from './PaymentModal.styles'

const PaymentModal = () => {
  const dispatch = useDispatch()
  const themes = useTheme()
  const finalTotal = useSelector(finalTotalSelector)
  const cart = useSelector(state => state.checkout.cart)
  const paymentStatus = useSelector(state => state.checkout.paymentStatus)
  const paymentMethod = useSelector(state => state.checkout.paymentMethod)

  const { t } = useTranslation()

  const formatPrice = (amount) => formatAmount(amount, themes.currency)

  const handlePayment = useCallback((method) => {
    dispatch(setPaymentMethod(method))
    dispatch(setPaymentStatus('PROCESSING'))

    setTimeout(() => {
      dispatch(setPaymentStatus('SUCCESS'))
      dispatch(setCurrentModal('CONFIRMATION'))
      sendOrder(cart)
    }, 2000)
  }, [dispatch, finalTotal, cart])

  const handleClose = () => {
    if (paymentStatus !== 'PROCESSING') {
      dispatch(setPaymentStatus('DEFAULT'))
      dispatch(setCurrentModal(null))
    }
  }

  const methodIcon = (method) => (method === 'CARD' ? <CardIcon /> : <CashIcon />)

  return (
    <ModalBackdrop onClick={handleClose}>
      <ModalWrapper onClick={e => e.stopPropagation()}>
        {paymentStatus === 'PROCESSING' ? (
          <ModalContent $processing>
            <ProcessingRing>
              <ProcessingSpinner />
              {methodIcon(paymentMethod)}
            </ProcessingRing>
            <PaymentProcessingText>
              {paymentMethod === 'CARD' ? t('paymentModal.processingCard') : t('paymentModal.processingCash')}
            </PaymentProcessingText>
            <ModalTotal $small>{formatPrice(finalTotal)}</ModalTotal>
          </ModalContent>
        ) : (
          <ModalContent>
            <CloseButton onClick={handleClose} aria-label={t('cartPanel.cancel')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M7 7l10 10M17 7L7 17" />
              </svg>
            </CloseButton>
            <ModalHeader>{t('paymentModal.selectMethod')}</ModalHeader>
            <ModalTotal>{formatPrice(finalTotal)}</ModalTotal>
            <PaymentMethods>
              <PaymentMethodButton onClick={() => handlePayment('CARD')}>
                <span className="icon"><CardIcon /></span>
                <span className="label">{t('paymentModal.card')}</span>
              </PaymentMethodButton>
              <PaymentMethodButton onClick={() => handlePayment('CASH')}>
                <span className="icon"><CashIcon /></span>
                <span className="label">{t('paymentModal.cash')}</span>
              </PaymentMethodButton>
            </PaymentMethods>
          </ModalContent>
        )}
      </ModalWrapper>
    </ModalBackdrop>
  )
}

const CardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
    <path d="M2.5 9.5h19M6.5 15h3.5M13 15h1.5" />
  </svg>
)

const CashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <circle cx="12" cy="12" r="2.75" />
    <path d="M5.5 9.5v.01M18.5 14.5v.01" />
  </svg>
)

export default PaymentModal
